import { Session } from '../AuthContext';
import { PermissionKey, PermissionLevel } from '../../system/permissions';

interface CreateParams {
  propertyType?: string;
  listAgent?: string;
  allowOnlineApplication?: boolean;
  iam?: string;
}

const IAM_STORAGE_KEY = 'ngw:iam';

/**
 * Persist iam to sessionStorage when found in URL, or recover it when absent.
 * sessionStorage is tab-scoped so it survives in-app navigation and refresh
 * but clears when the tab is closed.
 */
const resolveIam = (urlIam: string | undefined): string | undefined => {
  if (urlIam) {
    sessionStorage.setItem(IAM_STORAGE_KEY, urlIam);
    return urlIam;
  }
  return sessionStorage.getItem(IAM_STORAGE_KEY) || undefined;
};

export const extractCreateParams = (): CreateParams => {
  if (typeof window === 'undefined') return {};

  const urlParams = new URLSearchParams(window.location.search);

  // Check if we have the new 'init' parameter
  const initParam = urlParams.get('init');

  if (initParam) {
    try {
      // Decode the base64 string to JSON
      const jsonString = atob(initParam);

      // Parse the JSON string to get our parameters object
      const params = JSON.parse(jsonString);

      return {
        propertyType: params.propertyType || undefined,
        listAgent: params.listAgent || undefined,
        allowOnlineApplication: params.allowOnlineApplication || undefined,
        iam: resolveIam(params.iam || undefined),
      };
    } catch (error) {
      console.error('Error decoding init parameter:', error);
      // Fall back to direct URL parameters if decoding fails
    }
  }

  // Fallback to direct URL parameters (for backward compatibility)
  const propertyType = urlParams.get('propertyType') || undefined;
  const listAgent = urlParams.get('listAgent') || undefined;
  const allowOnlineApplication =
    urlParams.get('allowOnlineApplication') === 'true' || undefined;
  const iam = urlParams.get('iam') || undefined;

  return {
    propertyType,
    listAgent,
    allowOnlineApplication,
    iam: resolveIam(iam),
  };
};

/** Clear persisted iam from sessionStorage (e.g., on logout) */
export const clearPersistedIam = (): void => {
  sessionStorage.removeItem(IAM_STORAGE_KEY);
};

export const isPersonalAssistant = (session: Session | null): boolean => {
  return session?.user?.agent?.MemberType === 'Personal Assistant';
};

export const getListAgentPermissionLevel = (
  session: Session | null,
): PermissionLevel | null => {
  if (!session?.permissions) return null;

  // Look for permission: Access Listing Management
  const accessListingManagementPermissions = session.permissions.filter(
    (permission) => permission.key === PermissionKey.ACCESS_LISTING_MANAGEMENT,
  );

  // Check for Brokerage level first (highest priority)
  const hasBrokerageLevel = accessListingManagementPermissions.some(
    (permission) => permission.level === PermissionLevel.BROKERAGE,
  );

  if (hasBrokerageLevel) return PermissionLevel.BROKERAGE;

  // Check for Individual level next
  const hasIndividualLevel = accessListingManagementPermissions.some(
    (permission) => permission.level === PermissionLevel.INDIVIDUAL,
  );

  if (hasIndividualLevel) return PermissionLevel.INDIVIDUAL;

  // Check for Office level
  const officePermissions = accessListingManagementPermissions.filter(
    (permission) => permission.level === PermissionLevel.OFFICE,
  );

  if (officePermissions.length > 0) return PermissionLevel.OFFICE;

  return null;
};

export const getOfficeKeysFromPermissions = (
  session: Session | null,
): string[] => {
  if (!session?.permissions) return [];

  // Look for Office level permissions
  const officePermissions = session.permissions.filter(
    (permission) =>
      permission.key === PermissionKey.ACCESS_LISTING_MANAGEMENT &&
      permission.level === PermissionLevel.OFFICE,
  );

  // Extract the office keys from the grantor party keys
  return officePermissions
    .filter((permission) => permission.grantorKey)
    .map((permission) => permission.grantorKey!);
};
